import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Approved, Card, LayerChip, PrimaryButton, SectionLabel, Unresolved, Columns, Col } from "@coachbrain/ui";
import { CoachShell, ErrorBox, Loading } from "../components/common";
import { useLoad } from "../lib/hooks";
import { en } from "../i18n/en";

export function ReviewRoute() {
  const { id, reviewId } = useParams({ strict: false }) as { id: string; reviewId: string };
  const navigate = useNavigate();
  const { api, data, error, reload, setData } = useLoad((a) => a.getReview(reviewId), [reviewId]);
  const [editing, setEditing] = useState(false);
  const [implication, setImplication] = useState<string | null>(null);
  if (error) return <CoachShell><ErrorBox message={error} onRetry={reload} /></CoachShell>;
  if (!data || !api) return <CoachShell><Loading /></CoachShell>;
  const r = data; const t = en.review;
  const approved = r.status === "approved";
  return (
    <CoachShell title={t.title} subtitle={approved ? t.approved : t.unapproved} right={<button type="button" className="cb-link-button" style={{ color: "#C9D2DE" }} onClick={() => void navigate({ to: `/clients/${id}` })}>{en.common.back}</button>} hideNav>
      <Columns kind="two">
      <Col area="left">
      {r.selfReport ? <Card tone="unresolved"><Unresolved>Self-report</Unresolved><span className="cb-small">{t.selfReport}</span></Card> : null}
      <Card><SectionLabel>{t.intent}</SectionLabel><div className="cb-h3">{r.intent.text}</div></Card>
      <Card className="cb-card-flush">
        <div><SectionLabel>{t.meaningful}</SectionLabel></div>
        {r.meaningfulWork.map((m) => <div key={m.entryId} className="cb-row"><div className="cb-row-main"><span className="cb-row-left">{m.label}</span></div><div className="cb-row-sub" style={{ color: "var(--cb-blue-text)", fontWeight: 700 }}>{m.summary}</div></div>)}
      </Card>
      <Card className="cb-card-flush" style={{ opacity: 0.8 }}>
        <div><SectionLabel>{t.other}</SectionLabel></div>
        {r.otherExposure.map((m) => <div key={m.entryId} className="cb-row-main cb-muted"><span>{m.label}</span><span className="cb-small">{m.summary}</span></div>)}
      </Card>
      </Col>
      <Col area="right">
      {r.deviations.length ? <Card><SectionLabel>{t.deviations}</SectionLabel>{r.deviations.map((d) => <div key={d.deviationId} className="cb-body">{d.text}{d.whyQuestionId ? <span className="cb-text-unresolved"> · why?</span> : null}</div>)}</Card> : null}
      <Card tone="provisional">
        <SectionLabel tone="violet" right={<LayerChip layer={r.layer} />}>{t.likely}</SectionLabel>
        {r.likelyAccomplished.map((l, i) => (
          <div key={i} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div className="cb-strong">{l.criterion} <span className={l.met === "met" ? "cb-text-emerald" : l.met === "not_met" ? "cb-text-unresolved" : "cb-muted"}>· {l.met.replace("_", " ")}</span></div>
            <div className="cb-small cb-muted">Signal: {l.signal}</div>
            <div className="cb-body">{l.interpretation}</div>
          </div>
        ))}
      </Card>
      <Card>
        <SectionLabel right={<LayerChip layer={r.layer} />}>{t.implication}</SectionLabel>
        {editing ? <textarea className="cb-textarea" value={implication ?? r.implication ?? ""} onChange={(e) => setImplication(e.target.value)} /> : <div className="cb-body">{r.implication ?? "—"}</div>}
      </Card>
      {approved ? <div><Approved /></div> : (
        <div className="cb-btn-row">
          <PrimaryButton tone="emerald" onClick={async () => { setData(await api.approveReview(r.id, implication !== null ? { implication } : undefined)); }}>{t.approve}</PrimaryButton>
          <PrimaryButton tone="outline" onClick={() => setEditing((e) => !e)}>{t.edit}</PrimaryButton>
        </div>
      )}
      </Col>
      </Columns>
    </CoachShell>
  );
}

export function ReviewsListRoute() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const { data, error, reload } = useLoad((a) => a.pendingReviews(id), [id]);
  if (error) return <CoachShell><ErrorBox message={error} onRetry={reload} /></CoachShell>;
  if (!data) return <CoachShell><Loading /></CoachShell>;
  return (
    <CoachShell title="Reviews waiting" subtitle={`${data.length} to approve`}>
      <Columns kind="cards">
      {data.length === 0 ? <Card><span className="cb-muted">Nothing waiting.</span></Card> : data.map((r) => <Card key={r.id} onClick={() => void navigate({ to: `/clients/${id}/review/${r.id}` })}><div className="cb-row-main"><span className="cb-h3">{r.intent.text}</span><Unresolved>Unapproved</Unresolved></div><span className="cb-small cb-muted">{r.implication}</span></Card>)}
      </Columns>
    </CoachShell>
  );
}
